import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiArchiveBox,
  HiArrowPath,
  HiMagnifyingGlass,
  HiOutlineXMark,
} from "react-icons/hi2";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const URL_FIN = "https://desarrollotecnologicoar.com/api5/reservas_estatus/Finalizado";
const URL_CANCELED = "https://desarrollotecnologicoar.com/api5/reservas_estatus/Cancelado";
const URL_END = "https://desarrollotecnologicoar.com/api5/reservas_estatus/Finalizar";
const URL_REJ = "https://desarrollotecnologicoar.com/api5/reservas_estatus/Rechazado";

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const safeFormat = (fecha, formato = "EEEE d 'de' MMMM yyyy") => {
  if (!fecha) return "Sin fecha";
  try {
    const d = typeof fecha === "string" ? parseISO(fecha) : new Date(fecha);
    if (isNaN(d.getTime())) return "Sin fecha";
    return format(d, formato, { locale: es });
  } catch {
    return "Sin fecha";
  }
};

const drawerAnimStyle = `
  @keyframes archivadosDrawerSlideIn {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }
  .archivados-detail-drawer {
    animation: archivadosDrawerSlideIn 0.24s ease-out;
  }
`;

function Archivados() {
  const navigate = useNavigate();
  const [archivedItems, setArchivedItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const statusFromUrl = (url) => decodeURIComponent(url.split("/").pop() || "");

    (async () => {
      try {
        const [finResp, canceledResp, endResp, rejResp] = await Promise.all([
          fetch(URL_FIN, { signal: controller.signal }),
          fetch(URL_CANCELED, { signal: controller.signal }),
          fetch(URL_END, { signal: controller.signal }),
          fetch(URL_REJ, { signal: controller.signal }),
        ]);
        for (const r of [finResp, canceledResp, endResp, rejResp]) {
          if (!r.ok) throw new Error(`HTTP ${r.status} en ${r.url}`);
        }
        const [finData, canceledData, endData, rejData] = await Promise.all([
          finResp.json(), canceledResp.json(), endResp.json(), rejResp.json(),
        ]);
        const tag = (url, arr) =>
          (Array.isArray(arr) ? arr : []).map((i) => ({ ...i, estatus_api: statusFromUrl(url) }));
        const merged = [
          ...tag(URL_FIN, finData),
          ...tag(URL_CANCELED, canceledData),
          ...tag(URL_END, endData),
          ...tag(URL_REJ, rejData),
        ].sort((a, b) => new Date(b.fecha_reserva) - new Date(a.fecha_reserva));
        setArchivedItems(merged);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Error al cargar datos:", e);
      }
    })();
    return () => controller.abort();
  }, []);

  const filteredItems = archivedItems.filter((item) =>
    `${item.ticket} ${item.empresa}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRestore = async (id) => {
    try {
      const response = await fetch(`https://desarrollotecnologicoar.com/api5/reactivar_reserva/${id}`, { method: "PUT" });
      if (!response.ok) throw new Error("Error al restaurar");
      setArchivedItems((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error restaurando la tarea:", error);
    }
  };

  const handleItemClick = (item) => { setSelectedItem(item); setDrawerOpen(true); };

  return (
    <div className="page-container flex flex-col items-center">
      <h1 className="page-title mb-6">Elementos Archivados</h1>

      {/* Search */}
      <div className="relative w-full max-w-lg mb-4">
        <HiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          className="input pl-10 w-full"
          placeholder="Buscar por ticket o empresa"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="card w-full max-w-lg max-h-[45vh] overflow-y-auto">
        {filteredItems.length === 0 ? (
          <p className="empty-state">No hay elementos archivados.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{item.ticket}</p>
                  <p className="text-sm text-gray-500 truncate">{item.empresa}</p>
                </div>
                <button
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 shrink-0 ml-3"
                  onClick={(e) => { e.stopPropagation(); handleRestore(item.id); }}
                >
                  <HiArrowPath className="h-4 w-4" />
                  Restaurar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Back button */}
      <button
        className="btn-ghost flex items-center gap-2 mt-5"
        onClick={() => navigate("/pendientes")}
      >
        <HiArchiveBox className="h-4 w-4" />
        Volver al inicio
      </button>

      {/* Detail Drawer */}
      {drawerOpen && (
        <>
          <style>{drawerAnimStyle}</style>
          <div className="overlay z-[1100]" onClick={() => setDrawerOpen(false)} />
          <aside className="archivados-detail-drawer fixed inset-y-0 right-0 z-[1110] h-full w-full max-w-md bg-white border-l border-zinc-200 shadow-2xl overflow-y-auto">
            {selectedItem ? (
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-zinc-900 break-words">
                      {selectedItem.empresa || "Sin empresa"}
                    </h2>
                    <p className="text-sm text-zinc-500 mt-1">
                      Detalles del servicio archivado
                    </p>
                  </div>
                  <button onClick={() => setDrawerOpen(false)} className="btn-icon">
                    <HiOutlineXMark className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4 py-2 border-b border-zinc-100">
                    <span className="text-sm text-zinc-500">Ticket</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-800">
                        {selectedItem.ticket || "Sin ticket"}
                      </span>
                      {selectedItem.id && (
                        <a
                          href={`/editar_solicitud/${selectedItem.id}/aprobados`}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          Ver solicitud
                        </a>
                      )}
                    </div>
                  </div>
                  {[
                    { label: "Equipo", value: selectedItem.equipo || "Sin equipo" },
                    { label: "Tipo de servicio", value: selectedItem.tipo_servicio || "Sin tipo" },
                    { label: "Localidad", value: selectedItem.localidad || "Sin localidad" },
                    { label: "Telefono", value: selectedItem.telefono || "Sin telefono" },
                    { label: "Estatus", value: selectedItem.estatus_api || "Sin estatus" },
                    { label: "Fecha", value: formatDate(selectedItem.fecha_reserva) },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-start justify-between gap-4 py-2 border-b border-zinc-100"
                    >
                      <span className="text-sm text-zinc-500">{row.label}</span>
                      <span className="text-sm font-medium text-zinc-800 text-right break-words">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <h3 className="section-title mb-3">Actividades</h3>
                  {selectedItem.actividades?.length > 0 ? (
                    <div className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3">
                      <ul className="list-disc list-inside space-y-1 text-sm text-zinc-700">
                        {selectedItem.actividades.map((act, idx) => (
                          <li key={idx} className="break-words">{act}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm text-zinc-500">
                      Sin actividades registradas.
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="section-title mb-3">Itinerario</h3>
                  {selectedItem.itinerario?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedItem.itinerario.map((it, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3"
                        >
                          <p className="text-sm font-semibold text-zinc-900">
                            {safeFormat(it.fecha, "EEEE d 'de' MMMM yyyy")}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-3 text-sm text-zinc-600">
                            <span>Entrada: {it.entrada || "Sin hora"}</span>
                            <span>Salida: {it.salida || "Sin hora"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-sm text-zinc-500">
                      No hay itinerario registrado.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6 text-sm text-zinc-500">Cargando detalles...</div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}

export default Archivados;
